import React, { FC, useEffect } from 'react'
import { PolaroidList } from 'components/SA/Polaroid'
import styles from '../CollectionDetails.module.scss'
import { ARTWORK_STATUS } from 'components/SA/Polaroid/helpers'
import useCollectionPolaroidList from '../hooks/useCollectionPolaroidList'
import { SAArtwork } from 'types/Artwork'
import usePageLoader from 'hooks/usePageLoader'

type CollectionPolaroidListProps = {
  artworks: ReadonlyArray<SAArtwork>
  collectionTitle: string
  algoliaIndice: string
  customActions?: (artwork: SAArtwork) => JSX.Element
  dataList?: 'collection-detail' | 'aa-selection'
  isCollectionPrintView?: boolean
}

const CollectionPolaroidList: FC<CollectionPolaroidListProps> = ({
  artworks,
  collectionTitle,
  algoliaIndice,
  customActions,
  dataList = 'collection-detail',
  isCollectionPrintView,
}) => {
  const { hideLoader } = usePageLoader()
  const { userID, algoliaUserToken, isLoading } = useCollectionPolaroidList()

  const handleCheckIsPrintForDataLayerEvent = ({ originalStatus, hasOpenEditions }) => {
    const isSold = originalStatus === ARTWORK_STATUS.SOLD
    const isUnavailable = originalStatus === ARTWORK_STATUS.UNAVAILABLE
    return (isSold || isUnavailable) && hasOpenEditions
  }

  const handleCheckArtworkToBuildLayout = ({
    artwork,
    isValid,
    validationPropAlias,
  }: {
    artwork: SAArtwork
    isValid?: boolean
    validationPropAlias?: string
  }) => {
    const { hasOpenEditions } = artwork
    if (validationPropAlias === 'isDimensionsVisible') {
      return true
    }

    return hasOpenEditions && !!isValid
  }

  // For private collections there is no algoliaResults in redux, if this component is loaded we can make isSet = true to not show the loading spinner anymore
  useEffect(() => {
    hideLoader()
  }, [])

  if (!artworks.length) {
    return (
      <div className={styles.noResults}>
        <h4 className='defaultTypographyH4 defaultTextCapitalize'>
          There are no artworks in this collection yet.
        </h4>
      </div>
    )
  }

  return (
    <PolaroidList
      algoliaIndice={algoliaIndice}
      algoliaUserToken={algoliaUserToken}
      dataApi='algolia'
      dataList={dataList}
      dataListId={dataList}
      dataListName={collectionTitle}
      artworks={artworks}
      isLoading={isLoading}
      userID={userID}
      onCheckIsPrintForDataLayerEvent={handleCheckIsPrintForDataLayerEvent}
      customStyle={styles.customPolaroidListContainer}
      customActions={customActions}
      isPrintsFromVisible={!isCollectionPrintView}
      isMediumMaterialVisible={!isCollectionPrintView}
      isDimensionsVisible={true}
      showPrintsFromInPrice={isCollectionPrintView}
      showOriginalPrice={isCollectionPrintView}
      hasPrints={isCollectionPrintView}
      onCheckArtworkToBuildLayout={handleCheckArtworkToBuildLayout}
    />
  )
}

export default CollectionPolaroidList
